import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchCompanies,
  fetchProfile,
  updateUserProfile,
  type UpdateProfileInput,
  type UserProfile,
} from '@/api/auth'
import { api } from '@/lib/api'
import { API_BASE_URL } from '@/lib/config'
import { useAuthStore } from '@/stores/useAuthStore'

export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: fetchProfile,
  })
}

export function useUpdateProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateProfileInput) => updateUserProfile(input),
    onSuccess: (data: UserProfile) => {
      queryClient.setQueryData(['profile'], data)
      queryClient.invalidateQueries({ queryKey: ['agent-status'] })
    },
  })
}

export function useUploadUserAvatar() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const token = useAuthStore.getState().accessToken
      const formData = new FormData()
      formData.append('avatar', file)
      const res = await fetch(`${API_BASE_URL}/api/user/avatar/`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || `upload failed: ${res.status}`)
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
  })
}

export function useDeleteUserAvatar() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await api.delete('/api/user/avatar/')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
  })
}

// =====================================================
// Changement de mot de passe (dj-rest-auth)
// =====================================================

export interface ChangePasswordInput {
  old_password: string
  new_password1: string
  new_password2: string
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (input: ChangePasswordInput) => {
      const { data } = await api.post('/api/auth/password/change/', input)
      return data
    },
  })
}

// =====================================================
// Horaire personnel de l'agent
// =====================================================

export interface AgentScheduleDay {
  id?: number
  day: number // 0 = Lundi ... 6 = Dimanche (ou 0 = Dimanche selon backend, à confirmer)
  open_time: string | null // "HH:MM" ou "HH:MM:SS"
  close_time: string | null
  is_active: boolean
}

interface AgentScheduleResponse {
  schedules: AgentScheduleDay[]
}

export function useAgentSchedule() {
  return useQuery({
    queryKey: ['agent-schedule'],
    queryFn: async () => {
      const { data } = await api.get<AgentScheduleResponse>('/api/user/schedule/')
      return data.schedules || []
    },
  })
}

export function useUpdateAgentSchedule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (schedules: AgentScheduleDay[]) => {
      const { data } = await api.put<AgentScheduleResponse>('/api/user/schedule/', {
        schedules,
      })
      return data.schedules || []
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['agent-schedule'], data)
    },
  })
}

export function useDeleteAgentSchedule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await api.delete('/api/user/schedule/')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-schedule'] })
    },
  })
}

export function useCompanies() {
  return useQuery({
    queryKey: ['companies'],
    queryFn: fetchCompanies,
    select: (data) => data.companies || [],
  })
}

/** Helper : la première company (cas mono-tenant). */
export function useFirstCompany() {
  const query = useCompanies()
  return {
    ...query,
    data: query.data?.[0],
  }
}
